/**
 * ProgettazioneStep - Configuratore tecnico per la progettazione dell'impianto FV
 *
 * Step BPM "Progettazione e Fattibilità": prefill da anagrafiche,
 * calcoli in tempo reale, validazione tecnica e gestione documenti obbligatori.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Settings,
  CheckCircle2,
  FileText,
  Save,
  RefreshCw,
  Loader2,
  Info,
  Wrench
} from 'lucide-react';
import PVDesignWorkspace from './PVDesignWorkspace';
import { FormSection } from './progettazione/FormSection';
import { RequiredDocumentsCard } from './progettazione/RequiredDocumentsCard';

export default function ProgettazioneStep({
  contractId,
  nodeId,
  stepCode = 'PROGETTAZIONE_FATTIBILITA',
  onComplete,
  readOnly = false
}) {
  // Stato
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({});
  const [nodeDetails, setNodeDetails] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState('configuratore');
  const [documents, setDocuments] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [bomData, setBomData] = useState(null);
  const [bomCalculations, setBomCalculations] = useState(null);
  const [configValidation, setConfigValidation] = useState(null);

  // Carica configurazione e dati dello step
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Dettagli nodo
      const nodeRes = await api.get(`/contract-nodes/${nodeId}`);
      setNodeDetails(nodeRes.data);
      setFormData(nodeRes.data.form_data || {});
      setRequiredDocuments(nodeRes.data.required_documents || []);

      // Documenti
      try {
        const docsRes = await api.get(`/contract-nodes/${nodeId}/documents`);
        setDocuments((docsRes.data || []).filter(d => d.is_latest && !d.is_deleted));
      } catch (docError) {
        setDocuments([]);
      }

      // Prefill dalle anagrafiche master (opzionale - non bloccante)
      try {
        const prefillRes = await api.get(`/master-data/prefill/${contractId}`);
        const prefillData = prefillRes.data?.prefill_data || {};

        const prefillMapping = {
          cliente_nome: prefillData.cliente_nome || prefillData.full_name,
          cliente_codice_fiscale: prefillData.cliente_codice_fiscale || prefillData.codice_fiscale,
          indirizzo_impianto: prefillData.sito_indirizzo || prefillData.indirizzo_impianto,
          comune: prefillData.sito_citta || prefillData.citta,
          provincia: prefillData.sito_provincia || prefillData.provincia,
          foglio_catastale: prefillData.foglio,
          particella_catastale: prefillData.particella,
          pod: prefillData.pod,
          potenza_disponibile: prefillData.power_available,
        };

        setFormData(prev => {
          const newData = { ...prev };
          Object.entries(prefillMapping).forEach(([key, value]) => {
            if (value && !newData[key]) {
              newData[key] = value;
            }
          });
          return newData;
        });
      } catch (prefillError) {
        // Il prefill è opzionale, continua senza
      }

      // Inizializza sezioni espanse
      const sections = nodeRes.data.form_sections || [];
      const expanded = {};
      sections.forEach(s => {
        expanded[s.id] = s.collapsed !== true;
      });
      setExpandedSections(expanded);

      setConfig(nodeRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [nodeId, contractId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Valori derivati calcolati (fallback sui calcoli del configuratore BOM se i campi manuali sono vuoti)
  const calculations = useMemo(() => {
    const moduliPotenzaWp = parseFloat(formData.moduli_potenza_wp) || 0;
    const moduliQuantita = parseInt(formData.moduli_quantita) || 0;
    const inverterPotenzaKw = parseFloat(formData.inverter_potenza_kw) || 0;
    const inverterQuantita = parseInt(formData.inverter_quantita) || 0;
    const batterieCapacitaKwh = parseFloat(formData.batterie_capacita_kwh) || 0;
    const batterieQuantita = parseInt(formData.batterie_quantita) || 0;

    const moduliPotenzaTotaleKwp = (moduliPotenzaWp * moduliQuantita) / 1000;
    const inverterPotenzaTotaleKw = inverterPotenzaKw * inverterQuantita;
    const batterieTotaleKwh = batterieCapacitaKwh * batterieQuantita;
    const rapportoDcAc = inverterPotenzaTotaleKw > 0
      ? (moduliPotenzaTotaleKwp / inverterPotenzaTotaleKw).toFixed(2)
      : 0;

    const conFallback = (manuale, dalBom) =>
      parseFloat(manuale) > 0 ? manuale : (dalBom ?? manuale);

    const rapportoFinale = parseFloat(rapportoDcAc) > 0 ? rapportoDcAc : (bomCalculations?.dcAcRatio || rapportoDcAc);

    return {
      moduli_potenza_totale_kwp: conFallback(moduliPotenzaTotaleKwp.toFixed(2), bomCalculations?.totalModuliPower),
      inverter_potenza_totale_kw: conFallback(inverterPotenzaTotaleKw.toFixed(2), bomCalculations?.totalInverterPower),
      batterie_capacita_totale_kwh: conFallback(batterieTotaleKwh.toFixed(2), bomCalculations?.totalStorageCapacity),
      rapporto_dc_ac: rapportoFinale,
      dc_ac_ratio: rapportoFinale,
      potenza_progettata_kwp: conFallback(moduliPotenzaTotaleKwp.toFixed(2), bomCalculations?.totalModuliPower),
    };
  }, [formData, bomCalculations]);

  // Filtri sezioni memoizzati per performance
  const datiSections = useMemo(() => {
    const formSections = config?.form_sections || [];
    return formSections
      .filter(s => ['dati_input', 'configurazione_impianto'].includes(s.id))
      .sort((a, b) => a.order - b.order);
  }, [config?.form_sections]);

  const verificheSections = useMemo(() => {
    const formSections = config?.form_sections || [];
    return formSections
      .filter(s => ['verifiche_tecniche', 'note_finali'].includes(s.id))
      .sort((a, b) => a.order - b.order);
  }, [config?.form_sections]);

  // NOTA: i valori calcolati sono derivati via useMemo (oggetto calculations).
  // NON vengono salvati in formData per evitare loop di render infiniti.
  // Vengono inclusi solo al salvataggio.

  // Dati visualizzati nei form: formData + valori calcolati (per i campi "calculated")
  const displayData = useMemo(() => ({ ...formData, ...calculations }), [formData, calculations]);

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  }, []);

  // Callback stabile per i dati del configuratore (evita loop di notifiche col child)
  const handleConfiguratorChange = useCallback((data) => {
    setBomData(data?.bom);
    setBomCalculations(data?.calculations || null);
  }, []);

  // Salva dati form (inclusi i valori calcolati)
  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        moduli_potenza_totale_kwp: calculations.moduli_potenza_totale_kwp,
        inverter_potenza_totale_kw: calculations.inverter_potenza_totale_kw,
        batterie_capacita_totale_kwh: calculations.batterie_capacita_totale_kwh,
        rapporto_dc_ac: calculations.rapporto_dc_ac,
        dc_ac_ratio: calculations.dc_ac_ratio,
        potenza_progettata_kwp: calculations.potenza_progettata_kwp,
      };
      await api.put(`/contract-nodes/${nodeId}/form-data`, { form_data: dataToSave });
      toast.success('Dati salvati con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Controlli di validazione
  const validationStatus = useMemo(() => {
    const hasModuli = formData.moduli_marca && formData.moduli_modello && formData.moduli_quantita;
    const hasInverter = formData.inverter_marca && formData.inverter_modello && formData.inverter_quantita;
    const hasConfig = formData.tipologia_impianto && formData.tipologia_installazione;
    const hasVerifiche = formData.esito_fattibilita;

    const hasAccumuloData = formData.accumulo_presente === 'no' ||
      (formData.batterie_marca && formData.batterie_capacita_kwh);

    // Verifica documenti obbligatori
    const schemaPlani = documents.find(d => d.document_type === 'schema_planimetrico' || d.document_type === 'SCHEMA_PLANIMETRICO');
    const schemaElettr = documents.find(d => d.document_type === 'schema_elettrico' || d.document_type === 'SCHEMA_ELETTRICO');
    const hasRequiredDocs = schemaPlani && schemaElettr;

    const dcAcRatio = parseFloat(calculations.rapporto_dc_ac);
    const dcAcStatus = dcAcRatio >= 1.0 && dcAcRatio <= 1.3 ? 'ok' :
                       dcAcRatio > 1.3 && dcAcRatio <= 1.5 ? 'warning' : 'ko';

    return {
      hasModuli,
      hasInverter,
      hasConfig,
      hasVerifiche,
      hasAccumuloData,
      hasRequiredDocs,
      dcAcStatus,
      canComplete: hasModuli && hasInverter && hasConfig && hasVerifiche && hasAccumuloData && hasRequiredDocs,
    };
  }, [formData, documents, calculations]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intestazione */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Settings className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Progettazione e Fattibilità</h2>
            <p className="text-sm text-muted-foreground">
              Configuratore Tecnico Impianto Fotovoltaico
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || readOnly}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salva
          </Button>
        </div>
      </div>

      {/* Stato validazione */}
      <Alert variant={validationStatus.canComplete && configValidation?.canComplete ? 'default' : 'destructive'}>
        <Info className="h-4 w-4" />
        <AlertTitle>Stato Completamento Step</AlertTitle>
        <AlertDescription>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge variant={configValidation?.canComplete ? 'default' : 'outline'}>
              {configValidation?.canComplete ? '✓' : '○'} Configurazione Impianto
            </Badge>
            <Badge variant={validationStatus.hasVerifiche ? 'default' : 'outline'}>
              {validationStatus.hasVerifiche ? '✓' : '○'} Esito Fattibilità
            </Badge>
            <Badge variant={validationStatus.hasRequiredDocs ? 'default' : 'outline'}>
              {validationStatus.hasRequiredDocs ? '✓' : '○'} Documenti Obbligatori
            </Badge>
          </div>
        </AlertDescription>
      </Alert>

      {/* Tab principali */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start">
          <TabsTrigger value="configuratore" className="gap-2">
            <Wrench className="h-4 w-4" />
            Configuratore Impianto
            {!configValidation?.canComplete && bomData?.items?.length > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs">!</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="dati" className="gap-2">
            <Settings className="h-4 w-4" />
            Dati Progetto
          </TabsTrigger>
          <TabsTrigger value="verifiche" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Verifiche
          </TabsTrigger>
          <TabsTrigger value="documenti" className="gap-2">
            <FileText className="h-4 w-4" />
            Documenti
            {!validationStatus.hasRequiredDocs && (
              <Badge variant="destructive" className="ml-1 text-xs">!</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="configuratore" className="mt-4">
          <PVDesignWorkspace
            contractId={contractId}
            nodeId={nodeId}
            readOnly={readOnly}
            onConfigChange={handleConfiguratorChange}
            onValidationChange={setConfigValidation}
            contractAddress={nodeDetails?.contract?.indirizzo_fornitura}
          />
        </TabsContent>

        <TabsContent value="dati" className="space-y-4 mt-4">
          {datiSections.map(section => (
            <FormSection
              key={section.id}
              section={section}
              isExpanded={expandedSections[section.id] !== false}
              onToggle={() => toggleSection(section.id)}
              formData={displayData}
              onFieldChange={handleFieldChange}
              readOnly={readOnly}
            />
          ))}
        </TabsContent>

        <TabsContent value="verifiche" className="space-y-4 mt-4">
          {verificheSections.map(section => (
            <FormSection
              key={section.id}
              section={section}
              isExpanded={expandedSections[section.id] !== false}
              onToggle={() => toggleSection(section.id)}
              formData={displayData}
              onFieldChange={handleFieldChange}
              readOnly={readOnly}
            />
          ))}
        </TabsContent>

        <TabsContent value="documenti" className="mt-4">
          <RequiredDocumentsCard
            requiredDocuments={requiredDocuments}
            documents={documents}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
